'use client'

import React from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import { cn } from '@/lib/utils'

type BreadcrumbVariant = 'default' | 'dark' | 'light'
type BreadcrumbMap = Record<string, string>

interface BreadcrumbProps {
  titleCustom?: string
  descriptionCustom?: string
  variant?: BreadcrumbVariant
  breadcrumbMap?: BreadcrumbMap
  hiddenPaths?: string[]
  isIndustryView?: boolean
  isCustomPath?: boolean
}

const getStylesByVariant = (variant: BreadcrumbVariant) => ({
  list: cn(
    'hover:text-yellow02 font-thin',
    {
      'text-gray02': variant === 'dark',
      'text-black': variant === 'light'
    }
  ),
  lastItem: cn(
    'font-semibold',
    variant === 'dark' ? 'text-yellow02' : 'text-dark02'
  ),
  title: cn(
    'text-3xl md:text-4xl lg:text-5xl font-bold uppercase leading-tight m-0',
    {
      'text-white': variant === 'dark',
      'text-dark02': variant === 'light'
    }
  ),
  description: cn(
    'text-justify align-middle text-sm md:text-base',
    {
      'text-gray02': variant === 'dark',
      'text-dark02': variant === 'light'
    }
  )
})

const formatBreadcrumbTitle = (slug: string, breadcrumbMap: BreadcrumbMap): string => {
  return breadcrumbMap[slug] || slug.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
}

const getBreadcrumbHref = (
  paths: string[],
  currentIndex: number,
  isProductPage: boolean,
  isIndustryView: boolean
): string => {
  let urlPaths = paths.slice(0, currentIndex + 1)

  if (isProductPage) {
    if (isIndustryView) {
      urlPaths = urlPaths.filter(path => path !== 'productos-nuevos')
      return `/productos-nuevos/${urlPaths.join('/')}`
    } else {
      urlPaths = urlPaths.filter(path => path !== 'marcas')
      return `/productos-por-marca/${urlPaths.join('/')}`
    }
  }

  return `/${urlPaths.join('/')}`
}

export default function NextBreadcrumb({
  titleCustom,
  descriptionCustom,
  variant = 'default',
  breadcrumbMap = {},
  hiddenPaths = [],
  isIndustryView = false,
  isCustomPath = false
}: BreadcrumbProps) {
  const pathname = usePathname()
  const pathNames = pathname.split('/').filter(Boolean)
  const isProductPage = pathNames.includes('producto-nuevo')

  const visiblePathNames = isCustomPath
  ? Object.keys(breadcrumbMap)
  : isProductPage
    ? isIndustryView
      ? ['productos-nuevos', ...Object.keys(breadcrumbMap).filter(key => key !== 'productos-nuevos')]
      : ['marcas', ...Object.keys(breadcrumbMap).filter(key => key !== 'marcas')]
    : pathNames.filter(path => !hiddenPaths.includes(path))
  
  const styles = getStylesByVariant(variant)
  const title = titleCustom ?? formatBreadcrumbTitle(visiblePathNames[visiblePathNames.length - 1] || '', breadcrumbMap)

  return (
    <section className="pt-5 pb-5">
      <div className="flex flex-col gap-2">
        <div className="flex flex-col md:flex-row md:flex-wrap justify-between items-start md:items-center">
          <h1 className={cn(styles.title, "break-words max-w-full md:max-w-[60%] mb-2 md:mb-0")}>{title}</h1>
          <ul className="flex flex-wrap items-center text-sm md:text-base uppercase w-full md:w-auto">
            <li className={styles.list}>
              <Link href="/">Inicio</Link>
            </li>
            {visiblePathNames.map((link, index) => {
              if (!isCustomPath && index === visiblePathNames.length - 1) return null

              const href = getBreadcrumbHref(visiblePathNames, index, isProductPage, isIndustryView)
              const itemLink = formatBreadcrumbTitle(link, breadcrumbMap)

              return (
                <React.Fragment key={index}>
                  <span className={cn('mx-1 md:mx-2 flex-shrink-0', variant === 'dark' ? 'text-gray02' : '')}>/</span>
                  <li className={cn(styles.list, "truncate max-w-[200px] md:max-w-[250px] lg:max-w-[300px]")}>
                    <Link href={href} className={cn(styles.list, "truncate block")} title={itemLink}>{itemLink}</Link>
                  </li>
                </React.Fragment>
              )
            })}
          </ul>
        </div>
        {descriptionCustom && (
          <div className="flex w-full pt-2">
            <p className={styles.description}>{descriptionCustom}</p>
          </div>
        )}
      </div>
    </section>
  )
}
